import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { RagBadge } from "@/components/common/RagBadge";
import { InviteLinkResult } from "@/components/common/InviteLinkResult";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const CLIENT_ROLES = [
  { value: "client_admin", label: "Client Administrator" },
  { value: "client_manager", label: "Client Manager" },
  { value: "client_team_member", label: "Client Team Member" },
  { value: "read_only", label: "Read-only User" },
];
const ROLE_RAG = { amden_super_admin: "red", amden_consultant: "blue", client_admin: "green", client_manager: "blue", client_team_member: "grey", read_only: "grey" };

export default function Users() {
  const { orgId, currentOrg } = useOrg();
  const { user, isAmden, isSuper } = useAuth();
  const [users, setUsers] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [reassignTo, setReassignTo] = useState("none");

  const targetOrg = orgId || user?.organisation_id;
  const canManage = ["amden_super_admin", "amden_consultant", "client_admin"].includes(user?.role);

  const load = () => {
    const url = isSuper && !targetOrg ? "/users" : `/users?org_id=${targetOrg}`;
    api.get(url).then((r) => setUsers(r.data));
  };
  useEffect(() => { if (targetOrg || isSuper) { setUsers(null); load(); } }, [targetOrg]);

  useEffect(() => {
    if (!deleteTarget) { setWorkload(null); setReassignTo("none"); return; }
    setWorkload(null); setReassignTo("none");
    api.get(`/users/${deleteTarget.id}/workload`).then((r) => setWorkload(r.data)).catch(() => setWorkload(null));
  }, [deleteTarget]);

  if (!users) return <PageLoading />;

  const deleteUser = async () => {
    try {
      const q = reassignTo !== "none" ? `?reassign_to=${reassignTo}` : "";
      await api.delete(`/users/${deleteTarget.id}/permanent${q}`);
      toast.success("User deleted");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader eyebrow="Team" title="Users & Permissions" subtitle={currentOrg ? `Manage the team at ${currentOrg.company_name}.` : "Manage platform users and financial access."} icon="UsersRound"
        actions={canManage && targetOrg && <Button onClick={() => setInviting(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="invite-user-btn"><Icons.UserPlus size={16} className="mr-1.5" /> Invite user</Button>} />

      {users.length === 0 ? <EmptyState icon="UsersRound" title="No users" description="Invite team members to collaborate." /> : (
        <div className="amden-card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#E4E8EF] text-left text-xs uppercase tracking-wide text-[#1E2D44]/45">
                <th className="px-6 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Financial access</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-[#EEF1F6]" data-testid={`user-row-${u.id}`}>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFB000] text-xs font-bold text-[#0B1729]">{u.name[0]}</span>
                        <div><div className="font-medium text-[#0B1729]">{u.name}</div><div className="text-xs text-[#1E2D44]/50">{u.email}</div></div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <RagBadge rag={ROLE_RAG[u.role]}>{u.role_label}</RagBadge>
                      {u.people_admin && <RagBadge rag="amber" dot={false} className="ml-1.5" testId={`people-admin-badge-${u.id}`}>People admin</RagBadge>}
                    </td>
                    <td className="px-4 py-3.5">{u.financial_access || ["amden_super_admin", "amden_consultant"].includes(u.role) ? <span className="text-[#3D8B65]">Granted</span> : <span className="text-[#1E2D44]/40">Restricted</span>}</td>
                    <td className="px-4 py-3.5">{u.active ? <RagBadge rag="green">Active</RagBadge> : <RagBadge rag="grey">Inactive</RagBadge>}</td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {canManage && u.id !== user?.id && u.role !== "amden_super_admin" && (isSuper || u.role !== "amden_consultant") && <Button variant="ghost" size="sm" onClick={() => setEditing(u)} data-testid={`edit-user-${u.id}`}><Icons.Pencil size={14} /></Button>}
                        {isSuper && u.id !== user?.id && <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(u)} className="text-[#C95555] hover:bg-[#C95555]/10 hover:text-[#b34848]" data-testid={`delete-user-${u.id}`}><Icons.Trash2 size={14} /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {inviting && <InviteDialog orgId={targetOrg} onClose={() => setInviting(false)} onSaved={() => { setInviting(false); load(); }} />}
      {editing && <EditDialog u={editing} isSuper={isSuper} canGrantPeople={(isSuper || user?.role === "client_admin") && !editing.role.startsWith("amden")} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>{deleteTarget?.name} ({deleteTarget?.email}) will be permanently removed and can no longer sign in. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          {workload?.open_actions > 0 && (
            <div className="rounded-lg border border-[#FFB000]/40 bg-[#FFB000]/8 p-3">
              <p className="text-sm text-[#1E2D44]/80"><span className="font-semibold text-[#0B1729]">{workload.open_actions}</span> open action(s) are owned by this user.</p>
              <label className="mt-2 block text-xs font-medium text-[#1E2D44]/70">Reassign their open actions to</label>
              <Select value={reassignTo} onValueChange={setReassignTo}>
                <SelectTrigger className="mt-1 bg-white" data-testid="reassign-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Leave unassigned</SelectItem>
                  {(workload.candidates || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.role_label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteUser} className="bg-[#C95555] hover:bg-[#b34848]" data-testid="confirm-delete-user">Delete user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InviteDialog({ orgId, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", role: "client_manager", title: "", financial_access: false });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const save = async () => {
    if (!form.name || !form.email) return toast.error("Add name and email");
    setSaving(true);
    try {
      const res = await api.post(`/users?org_id=${orgId}`, form);
      toast.success("User invited");
      setResult({ ...res.data, name: form.name });
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const done = () => { onSaved(); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{result ? "Invite sent" : "Invite team member"}</DialogTitle></DialogHeader>
        {result ? (
          <>
            <InviteLinkResult name={result.name} result={result} />
            <DialogFooter>
              <Button onClick={done} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="invite-done-btn">Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="invite-name" /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="invite-email" /></div>
              </div>
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" /></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1" data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLIENT_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-[#E4E8EF] p-3">
                <div><div className="text-sm font-medium">Financial access</div><div className="text-xs text-[#1E2D44]/50">Can view revenue, profit, cash and costs</div></div>
                <Switch checked={form.financial_access} onCheckedChange={(v) => setForm({ ...form, financial_access: v })} data-testid="invite-financial" />
              </div>
              <p className="flex items-center gap-1.5 text-xs text-[#1E2D44]/45"><Icons.Mail size={13} /> They'll receive a secure email link to set their own password.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-invite-btn">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Send invite"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ u, isSuper, canGrantPeople, onClose, onSaved }) {
  const [name, setName] = useState(u.name || "");
  const [email, setEmail] = useState(u.email || "");
  const [role, setRole] = useState(u.role);
  const [financial, setFinancial] = useState(u.financial_access);
  const [peopleAdmin, setPeopleAdmin] = useState(!!u.people_admin);
  const [active, setActive] = useState(u.active);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const save = async () => {
    if (!name.trim()) return toast.error("Name can't be empty");
    if (!email.trim()) return toast.error("Email can't be empty");
    if (password && password.length < 8) return toast.error("Password must be at least 8 characters");
    setSaving(true);
    const payload = { name: name.trim(), email: email.trim(), role, financial_access: financial, active };
    if (isSuper && password) payload.password = password;
    if (canGrantPeople) payload.people_admin = peopleAdmin;
    try {
      await api.patch(`/users/${u.id}`, payload);
      toast.success(isSuper && password ? "User updated & password set" : "User updated");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const resend = async () => {
    setResending(true);
    try {
      const res = await api.post(`/users/${u.id}/resend-invite`);
      toast.success(res.data.email_sent ? "Set-password link emailed" : "Link generated — email delivery not configured");
      if (!res.data.email_sent && res.data.invite_link) {
        try { await navigator.clipboard.writeText(res.data.invite_link); toast.success("Link copied to clipboard"); } catch {}
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setResending(false); }
  };
  const roles = u.role.startsWith("amden") ? [{ value: u.role, label: u.role_label }] : CLIENT_ROLES;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Edit {u.name}</DialogTitle></DialogHeader>
        <div className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="edit-user-name" /></div>
            <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="edit-user-email" /></div>
          </div>
          <div><Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1" data-testid="edit-role"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-[#E4E8EF] p-3">
            <div className="text-sm font-medium">Financial access</div>
            <Switch checked={financial} onCheckedChange={setFinancial} data-testid="edit-financial" />
          </div>
          {canGrantPeople && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-[#E4E8EF] p-3">
              <div>
                <div className="text-sm font-medium">People administrator</div>
                <div className="text-xs text-[#1E2D44]/50">Manages employee records, invitations and confidential HR notes. Doesn't grant financial or strategy access.</div>
              </div>
              <Switch checked={peopleAdmin} onCheckedChange={setPeopleAdmin} data-testid="edit-people-admin" />
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg border border-[#E4E8EF] p-3">
            <div className="text-sm font-medium">Active</div>
            <Switch checked={active} onCheckedChange={setActive} data-testid="edit-active" />
          </div>
          {isSuper && (
            <div className="rounded-lg border border-[#E4E8EF] p-3">
              <Label className="text-sm">Set a new password</Label>
              <div className="relative mt-1">
                <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank to keep unchanged" className="pr-9" data-testid="edit-user-password" />
                <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#1E2D44]/45 hover:text-[#0B1729]" data-testid="toggle-user-password">
                  {showPw ? <Icons.EyeOff size={15} /> : <Icons.Eye size={15} />}
                </button>
              </div>
              <p className="mt-1.5 text-[11px] text-[#1E2D44]/45">Minimum 8 characters. Signs the user in immediately with it and cancels any pending set-password link.</p>
            </div>
          )}
          {u.active && (
            <button onClick={resend} disabled={resending} className="flex items-center gap-1.5 text-xs font-medium text-[#B87F1E] hover:underline disabled:opacity-50" data-testid="resend-invite-user">
              {resending ? <Icons.Loader2 className="animate-spin" size={13} /> : <Icons.MailPlus size={13} />} Resend set-password link{isSuper ? " instead" : ""}
            </button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-edit-user">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
